import { useSwipeToDelete } from '../hooks/useSwipeToDelete';
import { useTouchDevice } from '../hooks/useTouchDevice';

interface ConversationItemProps {
  id: string;
  title: string;
  updatedAt: string;
  isActive: boolean;
  isDeleting: boolean;
  onClick: () => void;
  onDelete: () => void;
}

export default function ConversationItem({
  id,
  title,
  updatedAt,
  isActive,
  isDeleting,
  onClick,
  onDelete
}: ConversationItemProps) {
  const isTouch = useTouchDevice();

  const swipeHandlers = useSwipeToDelete({
    onDelete,
    threshold: 80,
    enabled: isTouch
  });

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Delete this conversation?')) {
      onDelete();
    }
  };

  return (
    <div
      onClick={onClick}
      {...(isTouch ? swipeHandlers : {})}
      className={`group relative p-3 rounded-lg border cursor-pointer transition-colors ${
        isActive
          ? 'bg-accent-blue/10 border-accent-blue'
          : 'bg-dark-card border-dark-border hover:border-accent-blue'
      }`}
    >
      {/* Delete button - appears on hover or always on touch */}
      <button
        onClick={handleDeleteClick}
        disabled={isDeleting}
        className={`absolute top-2 right-2 p-1.5 bg-dark-bg/80 hover:bg-red-500/20 rounded-md border border-dark-border hover:border-red-500/50 transition-all disabled:opacity-50 z-10 ${
          isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        title="Delete conversation"
      >
        {isDeleting ? (
          <svg className="w-3.5 h-3.5 text-dark-text-muted animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-red-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        )}
      </button>

      <div className="text-sm text-dark-text truncate pr-8">
        {title || 'Untitled'}
      </div>
      <div className="text-xs text-dark-text-muted mt-1">
        {new Date(updatedAt).toLocaleDateString()} {new Date(updatedAt).toLocaleTimeString()}
      </div>
    </div>
  );
}
